import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, CheckCircle, XCircle } from 'lucide-react';
import { LocationModal } from '@/components/charging/LocationModal';
import { SortableLocationCard, LocationCard } from '@/components/charging/LocationCard';
import { DroppableColumn } from '@/components/charging/DroppableColumn';
import { BulkActionsToolbar } from '@/components/charging/BulkActionsToolbar';
import { KanbanFilters } from '@/components/charging/KanbanFilters';
import { QuickTagPopover } from '@/components/charging/QuickTagPopover';
import { DndContext, DragOverlay, DragStartEvent, DragEndEvent, closestCenter, closestCorners, PointerSensor, useSensor, useSensors, pointerWithin, rectIntersection, getFirstCollision, type CollisionDetection } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { KavelFaseStrip } from '@/components/charging/KavelFaseStrip';
import { SlapendSchakelaar } from '@/components/charging/SlapendSchakelaar';
import { useKavelSlaap } from '@/hooks/useKavelSlaap';
import { filterKavels, stadiumLabel, telKavels } from '@/lib/chargingStages';

// De labels komen sinds T2.5 uit `src/lib/chargingStages.ts`; alleen de kleur
// van een kanban-kolom is een keuze van dit scherm.
const STAGE_NIEUW = { id: 'nieuw', label: stadiumLabel('nieuw'), color: 'bg-blue-500' };

const STAGES_FLOW = [
  { id: 'evaluatie', label: stadiumLabel('evaluatie'), color: 'bg-yellow-500' },
  { id: 'uitwerking', label: stadiumLabel('uitwerking'), color: 'bg-purple-500' },
  { id: 'onderhandeling', label: stadiumLabel('onderhandeling'), color: 'bg-orange-500' },
  { id: 'contracting', label: stadiumLabel('contracting'), color: 'bg-green-500' },
];

export default function ChargingAcquisitie() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Determine portal context
  const basePath = location.pathname.startsWith('/crm') ? '/crm/charging' : '/charging';
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  const [toonSlapend, setToonSlapend] = useState(false);
  const [activeLocation, setActiveLocation] = useState<any>(null);
  const { slaapIn, maakWakker, archiveerVeel, wijzig, wijzigVeel } = useKavelSlaap();
  const columnRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [maxColumnHeight, setMaxColumnHeight] = useState(400);
  const isDraggingRef = useRef(false);
  const [tagPopup, setTagPopup] = useState<{ locationId: string; position: { x: number; y: number } } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Container IDs for collision detection priority
  const containerIds = [STAGE_NIEUW.id, ...STAGES_FLOW.map(s => s.id)];

  // Custom collision detection: prioritize stage columns over individual cards
  const containerFirstCollision: CollisionDetection = (args) => {
    // First check if pointer is within any stage column
    const pointerHits = pointerWithin(args);
    const stageHits = pointerHits.filter((entry) => containerIds.includes(String(entry.id)));
    
    if (stageHits.length > 0) {
      const overId = getFirstCollision(stageHits, 'id');
      if (overId) return [{ id: overId }];
    }
    
    // Fallback to item-level collision detection
    const fallbackHits = pointerHits.length > 0 ? pointerHits : rectIntersection(args);
    const overId = getFirstCollision(fallbackHits, 'id');
    return overId ? [{ id: overId }] : [];
  };

  const { data: locations = [], isLoading, isError, error } = useQuery({
    queryKey: ['charging-acquisitie-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select(`
          *,
          organizations(id, name)
        `)
        .in('stage', ['nieuw', 'evaluatie', 'uitwerking', 'onderhandeling', 'contracting'])
        .is('deleted_at', null)
        .order('order', { ascending: true });

      if (error) throw error;
      
      return (data || []).map(loc => ({
        ...loc,
        organization: loc.organizations
      }));
    },
  });

  // Dynamic column height synchronization - only when NOT dragging
  useEffect(() => {
    if (isDraggingRef.current) return; // Skip during drag
    
    const timer = setTimeout(() => {
      let maxHeight = 400;
      
      Object.values(columnRefs.current).forEach(ref => {
        if (ref) {
          const height = ref.scrollHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      });
      
      setMaxColumnHeight(maxHeight);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [locations]); // Removed activeLocation to prevent updates during drag

  const updateStageMutation = useMutation({
    mutationFn: async ({ locationId, newStage, insertBeforeId }: { locationId: string; newStage: string; insertBeforeId?: string }) => {
      let newOrder: number;

      if (insertBeforeId) {
        // Insert before specific location
        const { data: targetLocation } = await supabase
          .from('charging_locations')
          .select('order')
          .eq('id', insertBeforeId)
          .single();
        
        newOrder = targetLocation?.order || 0;
        
        // Shift other items down
        await supabase.rpc('shift_orders_down', {
          stage_name: newStage,
          from_order: newOrder
        });
      } else {
        // Append to end of stage
        const { data: maxOrderLocation } = await supabase
          .from('charging_locations')
          .select('order')
          .eq('stage', newStage)
          .is('deleted_at', null)
          .order('order', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        newOrder = (maxOrderLocation?.order || -1) + 1;
      }

      const { error } = await supabase
        .from('charging_locations')
        .update({ 
          stage: newStage, 
          order: newOrder,
          updated_at: new Date().toISOString() 
        })
        .eq('id', locationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-acquisitie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-realisatie-locations'] });
      toast({ title: 'Fase bijgewerkt' });
    },
    onError: (error) => {
      console.error('Update error:', error);
      toast({ title: 'Fout bij updaten', variant: 'destructive' });
    },
  });

  const reorderWithinColumnMutation = useMutation({
    mutationFn: async ({ locationId, targetLocationId, stage }: { locationId: string; targetLocationId: string; stage: string }) => {
      // Get all locations in this stage
      const { data: stageLocations } = await supabase
        .from('charging_locations')
        .select('id, order')
        .eq('stage', stage)
        .is('deleted_at', null)
        .order('order', { ascending: true });

      if (!stageLocations) return;

      const activeIndex = stageLocations.findIndex(l => l.id === locationId);
      const targetIndex = stageLocations.findIndex(l => l.id === targetLocationId);

      if (activeIndex === -1 || targetIndex === -1) return;

      // Reorder array
      const reordered = [...stageLocations];
      const [removed] = reordered.splice(activeIndex, 1);
      reordered.splice(targetIndex, 0, removed);

      // Batch update orders
      await Promise.all(
        reordered.map((loc, index) =>
          supabase
            .from('charging_locations')
            .update({ order: index, updated_at: new Date().toISOString() })
            .eq('id', loc.id)
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-acquisitie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-realisatie-locations'] });
    },
    onError: (error) => {
      console.error('Reorder error:', error);
      toast({ title: 'Fout bij herordenen', variant: 'destructive' });
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    const draggedLocation = locations.find(l => l.id === event.active.id);
    setActiveId(event.active.id as string);
    setActiveLocation(draggedLocation);
    isDraggingRef.current = true; // Mark as dragging
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    isDraggingRef.current = false; // Drag ended
    setActiveId(null);
    setActiveLocation(null);
    
    if (!over) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;
    
    const draggedLocation = locations.find((l: any) => l.id === activeId);
    if (!draggedLocation) {
      return;
    }

    const activeStage = draggedLocation.stage;
    
    // Check if dropped on a stage column or another location
    const ALL_STAGES = [STAGE_NIEUW, ...STAGES_FLOW];
    const isStageColumn = ALL_STAGES.some(s => s.id === overId);
    
    if (isStageColumn && overId !== activeStage) {
      // Moving to different stage (horizontal move)
      updateStageMutation.mutate({ 
        locationId: activeId, 
        newStage: overId
      });
    } else if (!isStageColumn) {
      // Dropped on another location (vertical reorder)
      const overLocation = locations.find((l: any) => l.id === overId);
      if (!overLocation) {
        setActiveId(null);
        return;
      }
      
      const overStage = overLocation.stage;
      const isSameColumn = activeStage === overStage;
      
      if (isSameColumn) {
        // Reordering within same column
        reorderWithinColumnMutation.mutate({
          locationId: activeId,
          targetLocationId: overId,
          stage: activeStage
        });
      } else {
        // Moving to different column + specific position
        updateStageMutation.mutate({
          locationId: activeId,
          newStage: overStage,
          insertBeforeId: overId
        });
      }
    }

    setActiveId(null);
  };

  // Scepticus-ronde 2: dit was de laatste knop op deze kaart die `if (!error)`
  // als bewijs nam. Een door RLS geblokkeerde update geeft geen error maar nul
  // rijen, en dan stond er "Gewonnen!" terwijl er niets was gebeurd.
  const handleMarkAsWon = async (locationId: string) => {
    try {
      await wijzig(locationId, { stage: 'contracting' }, 'Als gewonnen markeren');
      toast({ title: '✅ Gewonnen! Verplaatst naar Contracting' });
    } catch {
      // useKavelSlaap toont de foutmelding al.
    }
  };

  // T2.5 / scepticus-ronde — de rode X zet het stadium op 'verloren'.
  //
  // Hij ARCHIVEERDE (`deleted_at`), en daarmee was fase 4 vanuit de interface
  // onbereikbaar: de twee verloren Kavels in de database zijn ooit met de hand
  // op dat stadium gezet, en alles wat je hier verloor belandde in het
  // archief. De Verloren-sectie op /charging/archief was zo een vitrine
  // zonder ingang. Archiveren blijft bestaan, maar als eigen actie (de
  // bulkknop, en Verwijderen op de Kavel zelf) — verliezen en opruimen zijn
  // twee verschillende dingen.
  const handleMarkAsLost = async (locationId: string) => {
    try {
      await wijzig(locationId, { stage: 'verloren' }, 'Als verloren markeren');
      toast({
        title: 'Verplaatst naar Verloren',
        description: 'Niet gearchiveerd — terug te vinden onder Verloren op het Archief-scherm.',
      });
    } catch {
      // useKavelSlaap toont de foutmelding al.
    }
  };

  const handleMoveToConstruction = async (locationId: string) => {
    try {
      await wijzig(locationId, { stage: 'voorlopig_ontwerp' }, 'Naar Realisatie verplaatsen');
      toast({ title: '🚧 Verplaatst naar Realisatie!' });
    } catch {
      // useKavelSlaap toont de foutmelding al.
    }
  };

  // T2.5 / scepticus-ronde — telde niets en meldde altijd het volle aantal,
  // vier regels onder de bulk-delete die dat wél deed. `wijzigVeel` gebruikt
  // Promise.allSettled en telt wat er is geschreven.
  const handleBulkStageUpdate = async (newStage: string) => {
    await wijzigVeel(
      Array.from(selectedLocationIds),
      { stage: newStage },
      `verplaatst naar ${stadiumLabel(newStage)}`,
    );
    setSelectedLocationIds(new Set());
  };

  const handleBulkOwnerUpdate = async (ownerId: string) => {
    await wijzigVeel(Array.from(selectedLocationIds), { created_by: ownerId }, 'toegewezen');
    setSelectedLocationIds(new Set());
  };

  // Eén melding met de telling, ook bij volledig succes. `archiveerVeel` gaf
  // eerder N losse toasts en bij succes geen enkele samenvatting.
  const handleBulkDelete = async () => {
    await archiveerVeel(Array.from(selectedLocationIds));
    setSelectedLocationIds(new Set());
  };

  // Eerst zoeken/organisatie/tag, dan pas slapend. In die volgorde telt de
  // schakelaar over dezelfde set die je op het scherm hebt staan, en niet over
  // de hele portefeuille — anders zou "3 slapend verborgen" ernaast staan
  // terwijl die drie ook al door de zoekterm wegvielen.
  const gezochteLocations = locations.filter((location: any) => {
    const matchesSearch = !searchQuery ||
      location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.organization?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesOrg = organizationFilter === 'all' || location.org_id === organizationFilter;
    const matchesTag = !tagFilter || (location.tags && location.tags.includes(tagFilter));

    return matchesSearch && matchesOrg && matchesTag;
  });

  const telling = telKavels(gezochteLocations, { toonSlapend });
  const filteredLocations = filterKavels(gezochteLocations, { toonSlapend });

  const uniqueOrganizations = Array.from(
    new Map(
      locations
        .filter((l: any) => l.organization)
        .map((l: any) => [l.organization.id, l.organization])
    ).values()
  );

  const availableTags = Array.from(
    new Set(
      locations.flatMap((l: any) => l.tags || [])
    )
  ).sort();

  return (
    <div className="container mx-auto p-4 sm:p-6 w-full max-w-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Acquisitie Pipeline</h1>
            <p className="text-muted-foreground">Beheer en volg prospect locaties</p>
          </div>
          <Button onClick={() => setIsLocationModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nieuwe Locatie
          </Button>
        </div>

        {/* De vier fasen als hoofdindeling (T2.5) — de stadia zie je hieronder
            als kolommen, en op de Kavel zelf. */}
        <KavelFaseStrip actief="acquisitie" basePath={basePath} />

        <SlapendSchakelaar
          telling={telling}
          toonSlapend={toonSlapend}
          onToonSlapend={setToonSlapend}
        />

        {/* Filters */}
        <KanbanFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          cityFilter=""
          setCityFilter={() => {}}
          orgFilter={organizationFilter === 'all' ? '' : organizationFilter}
          setOrgFilter={(v) => setOrganizationFilter(v || 'all')}
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          cities={[]}
          organizations={uniqueOrganizations}
          availableTags={availableTags}
        />

        {/* Kanban Board */}
    <DndContext
      sensors={sensors}
      collisionDetection={containerFirstCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
          <div className="space-y-6">
            {/* Top Row: Nieuw (full width) */}
            <div className="w-full">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${STAGE_NIEUW.color}`}></div>
                  {STAGE_NIEUW.label}
                </h3>
                <Badge variant="secondary">
                  {filteredLocations.filter((l: any) => l.stage === STAGE_NIEUW.id).length}
                </Badge>
              </div>
              <DroppableColumn stage={STAGE_NIEUW}>
                <SortableContext
                  items={filteredLocations.filter((l: any) => l.stage === STAGE_NIEUW.id).map((l: any) => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {filteredLocations
                      .filter((l: any) => l.stage === STAGE_NIEUW.id)
                      .map((location: any) => (
                        <SortableLocationCard
                          key={location.id}
                          location={location}
                          onClick={() => navigate(`${basePath}/locatie/${location.id}`)}
                          stage={STAGE_NIEUW.id}
                          onMarkWon={handleMarkAsWon}
                          onMarkLost={handleMarkAsLost}
                          onMoveToConstruction={handleMoveToConstruction}
                          onSlaapIn={slaapIn}
                          onMaakWakker={maakWakker}
                          isSelected={selectedLocationIds.has(location.id)}
                          onSelectionChange={(checked) => {
                            const newSet = new Set(selectedLocationIds);
                            if (checked) {
                              newSet.add(location.id);
                            } else {
                              newSet.delete(location.id);
                            }
                            setSelectedLocationIds(newSet);
                          }}
                          onLongPress={(id, pos) => setTagPopup({ locationId: id, position: pos })}
                        />
                      ))}
                  </div>
                </SortableContext>
              </DroppableColumn>
            </div>

            {/* Bottom Row: Flow stages (4 columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
              {STAGES_FLOW.map((stage) => {
                const stageLocations = filteredLocations.filter((l: any) => l.stage === stage.id);
                
                return (
                  <div key={stage.id}>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>
                        {stage.label}
                      </h3>
                      <Badge variant="secondary" className="text-xs">
                        {stageLocations.length}
                      </Badge>
                    </div>
                    <DroppableColumn stage={stage} minHeight={maxColumnHeight}>
                      <SortableContext
                        items={stageLocations.map(l => l.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div 
                          ref={(el) => columnRefs.current[stage.id] = el}
                          className="space-y-2"
                          style={{ minHeight: `${maxColumnHeight}px` }}
                        >
                          {stageLocations.map((location: any) => (
                            <SortableLocationCard
                              key={location.id}
                              location={location}
                              onClick={() => navigate(`${basePath}/locatie/${location.id}`)}
                              stage={stage.id}
                              onMarkWon={handleMarkAsWon}
                              onMarkLost={handleMarkAsLost}
                              onMoveToConstruction={handleMoveToConstruction}
                              onSlaapIn={slaapIn}
                              onMaakWakker={maakWakker}
                              isSelected={selectedLocationIds.has(location.id)}
                              onSelectionChange={(checked) => {
                                const newSet = new Set(selectedLocationIds);
                                if (checked) {
                                  newSet.add(location.id);
                                } else {
                                  newSet.delete(location.id);
                                }
                                setSelectedLocationIds(newSet);
                              }}
                              onLongPress={(id, pos) => setTagPopup({ locationId: id, position: pos })}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DroppableColumn>
                  </div>
                );
              })}
            </div>
          </div>

          <DragOverlay>
            {activeLocation ? <LocationCard location={activeLocation} /> : null}
          </DragOverlay>
        </DndContext>

        {selectedLocationIds.size > 0 && (
          <BulkActionsToolbar
            selectedCount={selectedLocationIds.size}
            onClearSelection={() => setSelectedLocationIds(new Set())}
            onBulkStageUpdate={handleBulkStageUpdate}
            onBulkOwnerUpdate={handleBulkOwnerUpdate}
            onBulkDelete={handleBulkDelete}
            stages={[STAGE_NIEUW, ...STAGES_FLOW]}
          />
        )}

        {isLoading && <div className="text-center py-8">Laden...</div>}
        {isError && (
          <div className="text-center py-8 text-destructive">
            Fout bij laden: {error?.message}
          </div>
        )}
      </div>

      {/* Location Modal */}
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => {
          setIsLocationModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['charging-acquisitie-locations'] });
        }}
        initialStage="nieuw"
      />

      {/* Location Detail Dialog */}
      <Dialog open={!!selectedLocation} onOpenChange={() => setSelectedLocation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedLocation?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium">Adres</p>
              <p className="text-sm text-muted-foreground">{selectedLocation?.address}</p>
            </div>
            {selectedLocation?.organization && (
              <div>
                <p className="text-sm font-medium">Organisatie</p>
                <p className="text-sm text-muted-foreground">{selectedLocation.organization.name}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (selectedLocation) handleMarkAsWon(selectedLocation.id);
                  setSelectedLocation(null);
                }}
                className="flex-1"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Gewonnen
              </Button>
              <Button
                onClick={() => {
                  if (selectedLocation) handleMarkAsLost(selectedLocation.id);
                  setSelectedLocation(null);
                }}
                variant="destructive"
                className="flex-1"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Verloren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Tag Popover */}
      {tagPopup && (
        <QuickTagPopover
          open={!!tagPopup}
          onOpenChange={(open) => !open && setTagPopup(null)}
          locationId={tagPopup.locationId}
          locationName={locations.find((l: any) => l.id === tagPopup.locationId)?.name || ''}
          currentTags={locations.find((l: any) => l.id === tagPopup.locationId)?.tags || []}
          availableTags={availableTags}
          position={tagPopup.position}
          queryKey={['charging-acquisitie-locations']}
        />
      )}
    </div>
  );
}
